import { OPENROUTER_IMAGE_MODELS } from '@/components/DesignStudio/ImageEffectsProperties/core/openrouter/catalog.generated';

type Catalog = typeof OPENROUTER_IMAGE_MODELS;
export type OpenRouterImageModelId = keyof Catalog;
export type OpenRouterImageParam = 'aspectRatio' | 'resolution' | 'quality' | 'background';

type ParamsOf<M extends OpenRouterImageModelId> = {
    -readonly [K in keyof Catalog[M]['params']]: Catalog[M]['params'][K] extends readonly (infer V)[] ? V : never;
};

/** Model plus exactly the params OpenRouter supports for it, with their allowed values. */
export type OpenRouterImageModelParams = {
    [M in OpenRouterImageModelId]: { model: M } & ParamsOf<M>;
}[OpenRouterImageModelId];

interface ModelSpec {
    label: string;
    group: string;
    params: Partial<Record<OpenRouterImageParam, readonly string[]>>;
    defaults: Partial<Record<OpenRouterImageParam, string>>;
}

const MODELS: Partial<Record<string, ModelSpec>> = OPENROUTER_IMAGE_MODELS;

export const isOpenRouterImageModel = (model: unknown): model is OpenRouterImageModelId =>
    typeof model === 'string' && model in OPENROUTER_IMAGE_MODELS;

// Slugs saved before the current catalog; mirrors the storefront so saving the editor persists the fix.
const MODEL_REPLACEMENTS: Record<string, string> = {
    'gemini-2.5-flash-image': 'google/gemini-3.1-flash-image',
    'gemini-3.1-flash-image': 'google/gemini-3.1-flash-image',
    'gemini-3-pro-image': 'google/gemini-3-pro-image',
    'google/gemini-2.5-flash-image': 'google/gemini-3.1-flash-image',
    'openai/gpt-image-1.5': 'openai/gpt-image-2',
    'openai/gpt-image-1': 'openai/gpt-image-2.5-sunburst'
};

// gpt-image-2 has no transparent background; these rows rendered transparent before the model was retired.
const TRANSPARENT_MODEL_REPLACEMENTS: Record<string, string> = {
    'openai/gpt-image-1.5': 'openai/gpt-image-2.5-sunburst'
};

const resolveModel = (model: unknown, background: unknown) => {
    if (typeof model !== 'string') return model;
    const transparent = background === 'transparent' ? TRANSPARENT_MODEL_REPLACEMENTS[model] : undefined;
    return transparent ?? MODEL_REPLACEMENTS[model] ?? model;
};

// Rows saved before aspect ratios carried explicit pixel sizes, which OpenRouter mapped to these ratios.
const LEGACY_SIZE_ASPECT_RATIOS: Record<string, string> = {
    '1024x1024': '1:1',
    '1536x1024': '3:2',
    '1024x1536': '2:3',
    '1792x1024': '16:9',
    '1024x1792': '9:16',
    auto: 'auto'
};

/**
 * Brings an effect onto the current catalog: deprecated/retired models are remapped, legacy `size` becomes
 * `aspectRatio`, and any missing or unsupported value falls back to the model's default.
 */
export const normalizeOpenRouterImageParams = <T extends object>(effect: T): T => {
    const { size, aspectRatio, resolution, quality, background, ...rest } = effect as Record<string, unknown>;
    const model = resolveModel(rest.model, background);
    const spec = typeof model === 'string' ? MODELS[model] : undefined;
    if (!spec) return effect;

    const requested: Record<string, unknown> = {
        aspectRatio: aspectRatio ?? (typeof size === 'string' ? LEGACY_SIZE_ASPECT_RATIOS[size] : undefined),
        resolution,
        quality,
        background
    };
    const params = Object.fromEntries(
        Object.entries(spec.params).map(([param, values]) => {
            const value = requested[param];
            return [
                param,
                typeof value === 'string' && values?.includes(value)
                    ? value
                    : spec.defaults[param as OpenRouterImageParam]
            ];
        })
    );

    return { ...rest, model, ...params } as T;
};

export interface OpenRouterModelGroup {
    label: string;
    models: { id: OpenRouterImageModelId; label: string }[];
}

export const OPENROUTER_MODEL_GROUPS: OpenRouterModelGroup[] = Object.entries(OPENROUTER_IMAGE_MODELS).reduce<
    OpenRouterModelGroup[]
>((groups, [id, spec]) => {
    const group = groups.find((existing) => existing.label === spec.group);
    const model = { id: id as OpenRouterImageModelId, label: spec.label };
    if (group) group.models.push(model);
    else groups.push({ label: spec.group, models: [model] });
    return groups;
}, []);

/** The params the model supports, in catalog order, with their allowed values. */
export const openRouterModelParams = (model: OpenRouterImageModelId): [OpenRouterImageParam, readonly string[]][] =>
    Object.entries(MODELS[model]?.params ?? {}) as [OpenRouterImageParam, readonly string[]][];

/** Only the model and its catalog params, out of an effect already run through normalizeOpenRouterImageParams. */
export const pickOpenRouterModelParams = (effect: object): OpenRouterImageModelParams => {
    const { model, ...values } = effect as { model: OpenRouterImageModelId } & Partial<
        Record<OpenRouterImageParam, string>
    >;
    return {
        model,
        ...Object.fromEntries(openRouterModelParams(model).map(([param]) => [param, values[param]]))
    } as OpenRouterImageModelParams;
};
